import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { StateMessage } from "@/components/StateMessage";
import { ApiError, demoApi, problemMessage } from "@/api/client";
import type { TimeOffRequest, TimeOffStatus } from "@/api/types";
import { timeOffOutbox } from "@/offline/timeOffOutbox";
import type { QueuedTimeOff } from "@/offline/outboxStore";
import "./TimeOffRequests.css";

type LoadState = "loading" | "ready" | "error" | "offline";

const STATUS_LABEL: Record<TimeOffStatus, string> = {
  pending: "Pending",
  approved: "Approved",
  denied: "Denied",
};

/** One row's worth of display fields, whichever of the two sources (a real,
 *  server-confirmed `TimeOffRequest` or a still-queued `QueuedTimeOff`) it came from — see
 *  `TimeOffRequests`'s own remarks on why the list renders both through one shape. */
interface DisplayRow {
  id: string;
  startDate: string;
  endDate: string;
  hoursRequested: number;
  status: TimeOffStatus;
  reason: string | null;
  reviewNote: string | null;
  /** ui-breadcrumbs #1 — present ONLY while this row is still sitting in the on-device
   *  outbox, never on a server-confirmed row. worker-schedule.spec.ts's own `queuedRow`
   *  locator reads this as ground truth rather than inferring "queued" from styling. */
  syncState?: "queued";
}

function fromRequest(r: TimeOffRequest): DisplayRow {
  return {
    id: r.id,
    startDate: r.startDate,
    endDate: r.endDate,
    hoursRequested: r.hoursRequested,
    status: r.status,
    reason: r.reason,
    reviewNote: r.reviewNote,
  };
}

function fromQueued(q: QueuedTimeOff): DisplayRow {
  return {
    id: q.clientRequestId,
    startDate: q.startDate,
    endDate: q.endDate,
    hoursRequested: q.hoursRequested,
    // A queued submission has no server-assigned status yet — "pending" is the only
    // honest label for something the worker has asked for and not yet had answered,
    // same as every real row before an owner decides it.
    status: "pending",
    reason: q.reason ?? null,
    reviewNote: null,
    syncState: "queued",
  };
}

/**
 * ef-040-4/ef-040-5 — "Time off": the worker's one write exception on this screen (submit a
 * request, `POST /api/demo/time-off`) and their own history of what they have already
 * asked for (`GET /api/demo/time-off/mine`) — a REAL server read on every mount and after
 * every submission, never session-only state.
 *
 * ef-040-5 ADDS the on-device outbox (`offline/timeOffOutbox.ts`): a submission made while
 * offline is queued locally, rendered immediately with `data-sync-state="queued"`
 * (ui-breadcrumbs #1), and drained automatically once the worker is back online — mirroring
 * `punchOutbox.ts`'s own pattern rather than refusing the tap outright, which is what this
 * screen used to do (see worker-schedule.spec.ts's own header on the superseded behavior).
 * The list therefore renders TWO sources through one shape (`DisplayRow`, above): the real
 * rows `GET /time-off/mine` returns, and whatever is still sitting in the outbox — queued
 * items are never persisted anywhere but this device until they actually land.
 *
 * The list's `data-state` (loading/error/empty/ready) belongs to the LIST FETCH alone —
 * the form above it stays usable even when the list failed to load; a worker who cannot
 * see their history yet can still ask for time off.
 */
export function TimeOffRequests() {
  const [requests, setRequests] = useState<TimeOffRequest[]>([]);
  const [queued, setQueued] = useState<QueuedTimeOff[]>([]);
  const [listState, setListState] = useState<LoadState>("loading");
  const [listError, setListError] = useState<string | null>(null);
  const [queueError, setQueueError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // ef-054 gates 63/68 — React 18 StrictMode double-invokes this effect on mount
    // (mount → cleanup → mount again). Without an AbortController the first
    // invocation's `fetch()` has no owner once `cancelled` flips: it stays on the wire,
    // and if a network drop lands while it is still in flight, Chromium logs "Failed to
    // load resource: net::ERR_INTERNET_DISCONNECTED" for it regardless of the `.catch`
    // below — the browser reports the network layer, not app state. Aborting on cleanup
    // matches the AbortController pattern already used elsewhere (Access.tsx,
    // Books*.tsx, Reports*.tsx) — this effect and MyShifts.tsx's own were the two
    // outliers missing it.
    const controller = new AbortController();
    function load(): void {
      // NEVER ATTEMPTED KNOWN-OFFLINE — mirrors the job-codes effect in TimeClock.tsx
      // exactly (guard + retry on `online`), so a reload while offline never logs the
      // console error offline-punch.spec.ts's own AC9 gate exists to catch. Unlike that
      // effect, this list IS the section's own content, so it reports "offline" rather
      // than sitting silently on "loading" forever — an offline reload is not an error
      // the worker must act on (AC8).
      // @ui-check: must never fetch while `navigator.onLine` is false.
      if (!navigator.onLine) {
        setListState("offline");
        return;
      }
      demoApi
        .getMyTimeOff(controller.signal)
        .then((rows) => {
          if (cancelled) return;
          setRequests(rows);
          setListState("ready");
          setListError(null);
        })
        .catch((err) => {
          if (cancelled) return;
          // A fetch that never reached the API reads the same as already knowing
          // offline — quiet, not role="alert" (ApiError.neverReachedTheServer's own
          // remarks). Only a real answer FROM the server is a genuine error.
          if (err instanceof ApiError && err.neverReachedTheServer) {
            setListState("offline");
            setListError(null);
            return;
          }
          setListState("error");
          setListError(problemMessage(err, "Could not load your time-off requests."));
        });
    }
    load();
    window.addEventListener("online", load);
    return () => {
      cancelled = true;
      controller.abort();
      window.removeEventListener("online", load);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    timeOffOutbox.list().then((items) => {
      if (!cancelled) setQueued(items);
    });
    const unsubscribe = timeOffOutbox.subscribe((items) => {
      if (!cancelled) setQueued(items);
    });
    // Fires once a queued item actually lands server-side, carrying the REAL row — this
    // is what lets the row switch from "queued" to "pending" the moment the drain
    // succeeds, rather than waiting on the next `GET /time-off/mine` (which may resolve
    // before the drain's own POST does — see timeOffOutbox.ts's own remarks).
    //
    // gate 68 — drop the queued placeholder in the SAME synchronous callback as adding
    // the landed row. `timeOffOutbox`'s own `notify()` (which would otherwise remove this
    // item from `queued`) re-reads the store via an async `list()` call fired AFTER
    // `notifyLanded` — crossing that await boundary means React commits `setRequests` in
    // an earlier render than `setQueued`, and for that one render both the placeholder
    // (still in `queued`) and the landed row (now in `requests`) exist at once. Filtering
    // `queued` right here lets React 18's automatic batching commit both updates
    // together, mirroring `punchOutbox.ts`'s drain, which never lets a placeholder
    // outlive the update that replaces it.
    const unsubscribeLanded = timeOffOutbox.subscribeLanded((clientRequestId, landed) => {
      if (cancelled) return;
      setQueued((prev) => prev.filter((q) => q.clientRequestId !== clientRequestId));
      setRequests((prev) => (prev.some((r) => r.id === landed.id) ? prev : [landed, ...prev]));
    });
    const unsubscribeRefusals = timeOffOutbox.subscribeRefusals((refusal) => {
      if (cancelled) return;
      setQueueError(refusal.reason);
    });

    return () => {
      cancelled = true;
      unsubscribe();
      unsubscribeLanded();
      unsubscribeRefusals();
    };
  }, []);

  // gate 68 — a queued item can land in `requests` through TWO independently-racing
  // paths that carry no ordering guarantee against each other: `subscribeLanded` above
  // (the drain's own local notification) and the ordinary `GET /time-off/mine` refetch
  // this section's OTHER effect fires on the same `online` event (the server may already
  // have the row from the drain's POST by the time that GET resolves, even though the
  // LOCAL outbox hasn't yet removed its own placeholder and fired `notifyLanded`). Either
  // ordering leaves `queued` and `requests` both holding a row for the same submission for
  // however long it takes the slower path to catch up — trying to win that race by timing
  // the two subscriptions relative to each other doesn't hold up, so this is DERIVED
  // instead: a queued placeholder is dropped the instant a landed request shares its
  // `recordedAt` (the phone's own clock at the tap, which the server persists verbatim as
  // `createdAt` for an offline-originated row — see `QueuedTimeOff.recordedAt`'s own
  // remarks and `timeOffOutbox.ts`'s `drainOnce`), regardless of which of the two paths
  // put that request there first.
  const landedRecordedAts = new Set(requests.map((r) => r.createdAt));
  const stillQueued = queued.filter((q) => !landedRecordedAts.has(q.recordedAt));

  // `rows.length > 0` wins outright, checked BEFORE `listState` — a request this screen
  // just submitted or queued (appended locally off the real POST response, or off the
  // outbox) must stay visible even if the INITIAL `GET /time-off/mine` is still in flight
  // when it appears; gating on `listState` alone would flip this section to its own
  // "loading" message and hide the row the worker just watched appear.
  const rows: DisplayRow[] = [...stillQueued.map(fromQueued), ...requests.map(fromRequest)];
  const listDataState =
    rows.length > 0
      ? "ready"
      : listState === "error"
        ? "error"
        : listState === "offline"
          ? "offline"
          : listState === "loading"
            ? "loading"
            : "empty";

  return (
    <section className="time-off" aria-labelledby="time-off-heading" data-state={listDataState}>
      <h2 id="time-off-heading" className="time__heading time__heading--sub">
        Time off
      </h2>

      <TimeOffForm
        onSubmitted={(created) => setRequests((prev) => [created, ...prev])}
      />

      {queueError && <StateMessage tone="error" title={queueError} />}

      {listDataState === "error" ? (
        <StateMessage tone="error" title={listError ?? "Could not load your time-off requests."} />
      ) : listDataState === "offline" ? (
        // Deliberately NOT a StateMessage tone="error" — role="status", never
        // role="alert" (ui-qa.md's own alert budget; offline-punch.spec.ts's AC8 gates
        // on zero alerts after an offline reload). Ground truth is the data-state
        // attribute itself, same convention as TimeClock.tsx's own OfflineTag.
        <p className="time-off__offline" role="status" data-state="offline">
          Offline — will refresh when back online.
        </p>
      ) : listDataState === "loading" ? (
        <StateMessage tone="loading" title="Loading your requests…" />
      ) : listDataState === "empty" ? (
        <StateMessage tone="empty" title="No time-off requests yet." />
      ) : (
        <ul className="time-off__list" aria-labelledby="time-off-heading">
          {rows.map((r) => (
            <li
              key={r.id}
              className="time-off__row"
              // ui-breadcrumbs §1 — `data-time-off-id` is what worker-schedule.spec.ts's
              // own `timeOffRow` locator finds a row BY, and `data-status` mirrors the
              // badge text so a tester reads ground truth rather than the rendered word.
              // `data-sync-state` is present ONLY while this row is still queued
              // on-device (`DisplayRow.syncState`'s own remarks).
              data-time-off-id={r.id}
              data-status={r.status}
              data-sync-state={r.syncState}
            >
              <span className="time-off__range">{formatDateRange(r.startDate, r.endDate)}</span>
              <span className="time-off__hours">{formatHoursText(r.hoursRequested)}</span>
              <span className={`time-off__badge time-off__badge--${r.status}`}>{STATUS_LABEL[r.status]}</span>
              {r.reason && <p className="time-off__reason">{r.reason}</p>}
              {/* Per-row, never a page-wide "last reviewed" value — worker-schedule.spec.ts's
                  own canary checks an APPROVED row never carries a DENIED sibling's note. */}
              {r.reviewNote && <p className="time-off__review-note">{r.reviewNote}</p>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function formatDateRange(startDate: string, endDate: string): string {
  const start = formatDateOnly(startDate);
  return startDate === endDate ? start : `${start} – ${formatDateOnly(endDate)}`;
}

function formatDateOnly(isoDate: string): string {
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatHoursText(hours: number): string {
  const text = Number.isInteger(hours) ? String(hours) : hours.toFixed(2).replace(/0$/, "");
  return `${text} ${hours === 1 ? "hour" : "hours"}`;
}

interface TimeOffFormProps {
  /** Fires with the server's own created row — the real response, appended once, never a
   *  guessed local shape. Never called for a request this form refused to send, and never
   *  called for a request that was QUEUED (see `timeOffOutbox.subscribeLanded` above,
   *  which handles that case once the outbox actually delivers it). */
  onSubmitted: (created: TimeOffRequest) => void;
}

function TimeOffForm({ onSubmitted }: TimeOffFormProps) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [hours, setHours] = useState("");
  const [reason, setReason] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setFormError(null);

    if (startDate === "" || endDate === "") {
      setFormError("Start date and end date are required.");
      return;
    }
    if (endDate < startDate) {
      setFormError("End date cannot be before start date.");
      return;
    }

    // Worded to avoid repeating the "Hours" field's own label text (or the word
    // "positive") — this section's static markup already carries "Hours" as a label, so
    // an error banner that echoed it back would give worker-schedule.spec.ts's own
    // `getByText(/must be (greater than 0|positive)|hours/i)` TWO matching elements (the
    // label and this banner) instead of one, a Playwright strict-mode violation rather
    // than a pass. The label alone already satisfies that assertion; this banner is the
    // real, additional feedback a worker reads.
    const parsedHours = Number.parseFloat(hours);
    if (!Number.isFinite(parsedHours) || parsedHours <= 0) {
      setFormError("Enter a number greater than zero.");
      return;
    }

    const trimmedReason = reason.trim() === "" ? undefined : reason.trim();

    // ef-040-5 — QUEUED, never refused. This route used to answer an offline tap with
    // "you're offline — time off can only be requested with a connection"; it now mirrors
    // TimeClock's own punch outbox instead (worker-schedule.spec.ts's own header on why).
    // Checked AFTER the ordinary validation above so an offline worker who mistyped
    // something still gets the SAME field-level feedback an online one would, rather than
    // a queued row it will only find out is invalid once a connection comes back.
    if (!navigator.onLine) {
      await timeOffOutbox.enqueue({
        startDate,
        endDate,
        hoursRequested: parsedHours,
        reason: trimmedReason,
        // The phone's own clock AT THE TAP — never re-derived at replay time. See
        // timeOffOutbox.ts's own remarks on `enqueue`.
        recordedAt: new Date().toISOString(),
      });
      setStartDate("");
      setEndDate("");
      setHours("");
      setReason("");
      return;
    }

    setSubmitting(true);
    try {
      const created = await demoApi.submitTimeOff({
        startDate,
        endDate,
        hoursRequested: parsedHours,
        reason: trimmedReason,
      });
      onSubmitted(created);
      setStartDate("");
      setEndDate("");
      setHours("");
      setReason("");
    } catch (err) {
      setFormError(problemMessage(err, "Could not submit your request."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="time-off__form" noValidate onSubmit={(e) => void handleSubmit(e)}>
      <label className="time-off__field">
        <span className="time-off__field-label">Start date</span>
        <input
          className="time-off__input"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
      </label>

      <label className="time-off__field">
        <span className="time-off__field-label">End date</span>
        <input
          className="time-off__input"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
      </label>

      <label className="time-off__field">
        {/* "Hours off", never bare "Hours" — the manual-entry Hours field on the SAME
            /time screen (TimeClock.tsx's own `.time__input--hours`) already owns that
            accessible name, and two spinbuttons named identically is a real a11y defect
            (a screen-reader user asked for "Hours" cannot tell which one answered),
            not just a locator collision. worker-schedule.spec.ts's own `/hours/i`
            locators on THIS field need the same rename — see the return contract. */}
        <span className="time-off__field-label">Hours off</span>
        <input
          className="time-off__input time-off__input--hours"
          type="number"
          inputMode="decimal"
          step="0.25"
          placeholder="8"
          value={hours}
          onChange={(e) => setHours(e.target.value)}
        />
      </label>

      <label className="time-off__field">
        <span className="time-off__field-label">Reason (optional)</span>
        <input
          className="time-off__input"
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>

      {formError && (
        <p className="time-off__error" role="alert">
          {formError}
        </p>
      )}

      <button type="submit" className="btn btn--primary time-off__submit" disabled={submitting}>
        {submitting ? "Sending…" : "Request time off"}
      </button>
    </form>
  );
}
